import {
  Button,
  Card,
  Col,
  Divider,
  Icons,
  ListButton,
  Popconfirm,
  Row,
  Show,
  Table,
  TableProps,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { useCan, useNavigation } from '@pankod/refine-core';
import { useParams } from '@pankod/refine-react-router-v6';
import { ProductFooter } from 'components';
import CustomTableWithPagination from 'components/table/custom-table';
import { EStatus } from 'enums';
import { ISaleStockCorrection, PaginationHandler } from 'interfaces';
import { useEffect, useState } from 'react';
import { deleteSalesStockCorrection, getSalesStockCorrection } from 'services/sale';
import { formatCurrency } from 'utilities/shared';
import { calculateStoredSaleVatAmount, storedVatFractionToPercent } from 'utilities/sale-vat';

const { Title, Text } = Typography;

export const SalesStockCorrectionShow: React.FC = () => {
  const [sale, setSale] = useState<ISaleStockCorrection>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);

  const { id } = useParams();
  const { list } = useNavigation();

  useEffect(() => {
    if (!id) return;

    async function fetchSale(saleId: string) {
      setIsLoading(true);
      try {
        const response = await getSalesStockCorrection(saleId);
        setSale(response);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
      setIsLoading(false);
    }

    fetchSale(id);
  }, [id]);

  const { data: canAccessDelete } = useCan({
    resource: 'sales',
    action: 'delete',
    params: { field: 'id' },
  });

  const note = sale?.note;
  const clientPoc = sale?.clientPoc;
  const warehouse = sale?.warehouse;
  const subTotal = sale?.subTotal;
  // vatAmount is stored as a fraction (0.15 for 15%).
  const vatPercentage = storedVatFractionToPercent(sale?.vatAmount);
  const vatAmount = calculateStoredSaleVatAmount(subTotal, sale?.vatAmount);
  const shipmentCost = sale?.shipmentCost;
  const discountCost = sale?.discountCost;
  const netTotal = sale?.totalAmount;

  const products = sale?.stockCorrectionSalesProducts.map((sale) => ({
    id: sale.id,
    sku: sale.productVariant.sku,
    variantName: sale.productVariant.name,
    productName: sale.productVariant.productCopy.name,
    quantity: sale.quantity,
    unitPrice: sale.unitPrice,
    subTotal: formatCurrency(sale.subTotal),
  }));

  const isCompleted = sale?.salesStatus === EStatus.COMPLETED;

  const handleDelete = async (id?: string) => {
    if (!id) return;

    try {
      await deleteSalesStockCorrection(id);
      notification.success({
        message: 'Success',
        description: 'Successfully deleted a sale stock correction',
      });
      list('stock-correction-sales');
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to delete sale stock correction',
      });
    }
  };

  const pageHeader = !isLoading ? (
    <>
      <ListButton />
      <Popconfirm
        title="Are you sure?"
        okText="Delete"
        onConfirm={() => handleDelete(id)}
        disabled={!canAccessDelete?.can}
      >
        <Button
          size="middle"
          danger
          icon={<Icons.DeleteOutlined />}
          disabled={!canAccessDelete?.can}
          hidden={isCompleted}
        >
          Delete
        </Button>
      </Popconfirm>
    </>
  ) : null;

  const footer = () => {
    return (
      <ProductFooter
        subTotal={subTotal || 0}
        vatAmount={vatAmount}
        vatPercentage={vatPercentage || 0}
        shipmentCost={shipmentCost || 0}
        discountCost={discountCost || 0}
        netTotal={netTotal || 0}
      />
    );
  };

  const onTableChange: TableProps<any>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra,
  ) => {
    const page = pagination.current ?? 1;
    const limit = pagination.pageSize ?? 10;
    setPage(page);
    setLimit(limit);
  };

  const onPageChange: PaginationHandler = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  // Function to calculate the current page data
  const getCurrentPageData = () => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return products?.slice(startIndex, endIndex);
  };

  return (
    <>
      <Show isLoading={isLoading} headerProps={{ extra: pageHeader }}>
        {/* Sale Info */}
        <Row justify="space-around" align="middle">
          <Col xs={24} lg={12}>
            <Card title="Sale Information">
              <Title level={5}>Client Name</Title>
              <Text>{sale?.client.name}</Text>
              <Title level={5}>Warehouse</Title>
              <Text>{warehouse?.name}</Text>
              <Title level={5}>Point Of Contact</Title>
              <Text>{clientPoc?.name}</Text>
              <Title level={5}>Shipping Address</Title>
              <Text>{sale?.salesAddress}</Text>
              <Title level={5}>Note</Title>
              <Text>{note}</Text>
            </Card>
          </Col>
        </Row>
      </Show>

      <Divider />

      {/* Products Table */}
      <Row wrap={false}>
        <Col flex="auto">
          <CustomTableWithPagination
            total={sale?.stockCorrectionSalesProducts.length || 0}
            page={page}
            limit={limit}
            onPageChange={onPageChange}
          >
            <Table
              // dataSource={products}
              dataSource={getCurrentPageData()} // Pass sliced data
              rowKey="id"
              scroll={{ x: true }}
              footer={(_data) => footer()}
              onChange={onTableChange}
              pagination={false}
            >
              <Table.Column dataIndex="sku" title="SKU" />
              <Table.Column dataIndex="variantName" title="Variant Name" />
              <Table.Column dataIndex="productName" title="Product Name" />
              <Table.Column dataIndex="quantity" title="Quantity" />
              <Table.Column dataIndex="unitPrice" title="Unit Price" />
              <Table.Column dataIndex="subTotal" title="Total" />
            </Table>
          </CustomTableWithPagination>
        </Col>
      </Row>
    </>
  );
};
